import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

import { useServer } from "../../hooks/useServer";
import { useStorage } from "../../hooks/useStorage";

import Header from "../../components/livetutor/Header";
import { useLiveTutor } from "../../hooks/LiveTutorProvider";

import Step from "../../components/livetutor/lesson-components/Step";

declare global {
	interface Window {
		vadit: (onSpeechStart: () => void, onSpeechEnd: () => void) => Promise<any>;
	}
}

const Card = ({
	title,
	description,
	status,
	isLoading,
	isError,
	handleClick,
}: {
	title: string;
	description: string;
	status: string;
	isLoading: boolean;
	isError: any;
	handleClick: () => void;
}) => {
	return (
		<div
			className="bg-white p-4 rounded-lg shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100"
			onClick={handleClick}
		>
			<div className="flex justify-between items-center gap-4">
				<div className="flex-1">
					<h3 className="text-base font-semibold text-gray-800">{title}</h3>
					<p className="text-gray-600 text-xs mt-0.5">{description}</p>
				</div>
				<div className="flex-shrink-0">
					{status === "completed" ? (
						<div className="flex items-center text-green-500 text-xs">
							<svg
								className="w-4 h-4 mr-1"
								fill="none"
								stroke="currentColor"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									strokeWidth={2}
									d="M5 13l4 4L19 7"
								/>
							</svg>
							<span>Completed</span>
						</div>
					) : isLoading ? (
						<div className="flex items-center">
							<div className="animate-spin h-4 w-4 border-2 border-purple-500 border-t-transparent rounded-full"></div>
							<span className="ml-1 text-purple-500 text-xs">Loading</span>
						</div>
					) : isError ? (
						<span className="text-red-500 text-xs">Error</span>
					) : (
						<span className="text-blue-500 text-xs">Available</span>
					)}
				</div>
			</div>
		</div>
	);
};

const StartPage: React.FC = () => {
	const {
		show,
		actionHandler,
		exerciseInfoRef,
		lessonInfoRef,
		currentState,
		setCurrentState,
		completed,
		step,
		setStep,
	} = useLiveTutor();
	const {
		getLesson,
		getLessonByInterest,
		getPronunciation,
		getRoleplayScenario,
		postBasicLesson,
		postLessonByInterest,
		postRoleplayResults,
		user,
		addXp,
		checkStreak,
	} = useServer();

	const today = new Date().toLocaleDateString();
	exerciseInfoRef.current.completed = completed[today] || [];
	lessonInfoRef.current.steps = [
		{
			type: "start",
			completed: completed[today] || [],
		},
	];

	// Helper function to check if a lesson is completed for today
	const isLessonCompleted = (lessonType: string) => {
		const today = new Date().toLocaleDateString();
		return completed[today]?.includes(lessonType);
	};

	const {
		data: lesson,
		isLoading: isLessonLoading,
		error: isLessonError,
	} = useQuery({
		queryKey: ["general_lesson"],
		queryFn: getLesson, // will be updated to getBasicLesson or getAdvancedLesson
		staleTime: 999999999, // don't refetch
		refetchOnMount: false, // don't refetch on mount
		refetchOnReconnect: false, // don't refetch on reconnect
		retry: false, // retry failed requests up to 3 times
		enabled: Boolean(user && !isLessonCompleted("general_lesson")),
	});

	const {
		data: lesson_by_interest,
		isLoading: isLessonLoadingByInterest,
		error: isLessonErrorByInterest,
	} = useQuery({
		queryKey: ["general_lesson_by_interest"],
		queryFn: getLessonByInterest, // will be updated to getBasicLesson or getAdvancedLesson
		staleTime: 999999999, // don't refetch
		refetchOnMount: false, // don't refetch on mount
		refetchOnReconnect: false, // don't refetch on reconnect
		retry: false, // retry failed requests up to 3 times
		enabled: Boolean(user && !isLessonCompleted("general_lesson_by_interest")),
	});

	const {
		data: pronunciation_content,
		isLoading: isPronunciationLoading,
		error: isPronunciationError,
	} = useQuery({
		queryKey: ["pronunciation_lesson"],
		queryFn: getPronunciation,
		staleTime: 999999999, // don't refetch
		refetchOnMount: false, // don't refetch on mount
		refetchOnReconnect: false, // don't refetch on reconnect
		retry: false, // retry failed requests up to 3 times
		enabled: Boolean(user && !isLessonCompleted("pronunciation_lesson")),
	});

	const {
		data: conversation_content,
		isLoading: isRoleplayLoading,
		error: isRoleplayError,
	} = useQuery({
		queryKey: ["conversation_lesson"],
		queryFn: getRoleplayScenario,
		staleTime: 999999999,
		refetchOnMount: false,
		refetchOnReconnect: false,
		retry: false,
		enabled: Boolean(user && !isLessonCompleted("conversation_lesson")),
	});

	useEffect(() => {
		if (!lesson) return;
		actionHandler.current["start_general_scenario"] = () => {
			lessonInfoRef.current = lesson;
			setCurrentState("general_lesson");
		};
	}, [lesson]);

	useEffect(() => {
		if (!lesson_by_interest) return;
		actionHandler.current["start_interest_scenario"] = () => {
			lessonInfoRef.current = lesson_by_interest;
			setCurrentState("general_lesson_by_interest");
		};
	}, [lesson_by_interest]);

	useEffect(() => {
		if (!pronunciation_content) return;
		actionHandler.current["start_pronunciation_practice"] = () => {
			lessonInfoRef.current = pronunciation_content;
			setCurrentState("pronunciation_lesson");
		};
	}, [pronunciation_content]);

	useEffect(() => {
		if (!conversation_content) return;
		actionHandler.current["start_conversation_practice"] = () => {
			lessonInfoRef.current = conversation_content;
			setCurrentState("conversation_lesson");
		};
	}, [conversation_content]);

	return (
		<div className=" bg-gradient-to-br from-gray-50 to-white py-6 flex-1  overflow-y-auto">
			<div className="max-w-xl mx-auto w-full px-4">
				<h2 className="text-3xl font-bold mb-4 text-center bg-clip-text text-transparent bg-gradient-to-r from-blue-500 to-purple-500">
					What would you like to practice today?
				</h2>
				<div className="space-y-3">
					<Card
						title="General Practice"
						description="Sharpen your skills with daily exercises"
						status={isLessonCompleted("general") ? "completed" : "available"}
						isLoading={isLessonLoading}
						isError={isLessonError}
						handleClick={() => {
							actionHandler.current["start_general_scenario"]();
						}}
					/>
					<Card
						title="Learn About Your Interest"
						description="Deep dive into topics you love"
						status={isLessonCompleted("interest") ? "completed" : "available"}
						isLoading={isLessonLoadingByInterest}
						isError={isLessonErrorByInterest}
						handleClick={() => {
							actionHandler.current["start_interest_scenario"]();
						}}
					/>
					<Card
						title="Pronunciation Practice"
						description="Improve your pronunciation skills"
						status={
							isLessonCompleted("pronunciation") ? "completed" : "available"
						}
						isLoading={isPronunciationLoading}
						isError={isPronunciationError}
						handleClick={() => {
							actionHandler.current["start_pronunciation_practice"]();
						}}
					/>
					<Card
						title="Conversation Practice"
						description="Practice real-life conversations"
						status={
							isLessonCompleted("conversation") ? "completed" : "available"
						}
						isLoading={isRoleplayLoading}
						isError={isRoleplayError}
						handleClick={() => {
							actionHandler.current["start_conversation_practice"]();
						}}
					/>
				</div>
			</div>
		</div>
	);
};

const LiveTutor: React.FC = () => {
	const { loadFromStorage, saveToStorage, setLocalStorage } = useStorage();
	const {
		setSuggestion,
		show,
		currentState,
		setCurrentState,
		lessonInfoRef,
		step,
		finishCallbackRef,
	} = useLiveTutor();
	const totalSteps = lessonInfoRef.current.steps.length;

	useEffect(() => {
		(async () => {
			const completedOnboarding = await loadFromStorage(
				"completedOnboarding",
				false
			);

			if (!completedOnboarding) {
				lessonInfoRef.current = {
					type: "onboarding",
					steps: [
						{
							type: "set_native_language",
						},
						{
							type: "set_english_level",
						},
						{
							type: "set_goals",
						},
						{
							type: "set_interests",
						},
					],
				};
				setCurrentState("set_native_language");
				finishCallbackRef.current = () => {
					setLocalStorage("completedOnboarding", true);
				};
				return;
			}
		})();
	}, []);

	const exercise = currentState.includes("set")
		? "Onboarding"
		: currentState !== "start"
		? "Lesson"
		: "";

	return (
		<div className="overflow-y-hidden">
			<Header
				exercise={exercise}
				back={() => {
					console.log("Back");
					setCurrentState("start");
				}}
			/>
			<main className="relative flex flex-col pb-20 overflow-y-visible">
				<div className="fixed  w-full bg-gray-200 h-2">
					<div
						className={`h-full bg-blue-600 transition-all duration-300 ${
							step === totalSteps - 1 ? "" : "rounded-r"
						}`}
						style={{ width: `${((step + 1) / totalSteps) * 100}%` }}
					/>
				</div>
				{currentState === "start" && <StartPage />}

				{currentState !== "start" && (
					<Step stepInfo={lessonInfoRef.current.steps[step]} />
				)}
			</main>
		</div>
	);
};

export default LiveTutor;
